import { useEffect, useState } from 'react'

/**
 * Si accorge da sé quando è stata pubblicata una versione nuova di KPO.
 *
 * <p><b>Perché serve.</b> Dentro l'iframe di KBE ricaricare è scomodo: il tasto del browser
 * ricarica la pagina che ospita, non quella incorniciata, e la scorciatoia per forzare la cache
 * agisce sul documento sbagliato. Chi lavora finiva per non prendere mai le modifiche.</p>
 *
 * <p><b>Come lo capisce.</b> Ogni pubblicazione cambia il nome del pacchetto JavaScript, perché
 * contiene l'impronta del contenuto. Si confronta il nome del pacchetto che questa pagina sta
 * eseguendo con quello scritto nell'`index.html` sul server: se differiscono, c'è una versione
 * nuova. Nessun endpoint da mantenere, nessun numero di versione da ricordarsi di aggiornare —
 * l'impronta la calcola già la compilazione.</p>
 *
 * <p><b>`cache: 'no-store'`</b> non è un dettaglio: senza, la richiesta potrebbe essere servita
 * dalla cache del browser e vedremmo per sempre la versione vecchia — cioè il controllo
 * risponderebbe «tutto aggiornato» proprio nel caso in cui non lo è.</p>
 *
 * <p>Non ricarica da sé. Chi sta scrivendo quantità su una proposta non deve vedersi portare via
 * la pagina: si segnala e basta, e decide l'utente. Il controllo gira ogni cinque minuti e a ogni
 * ritorno sulla scheda, che è il momento in cui una pagina lasciata aperta è più probabilmente
 * vecchia.</p>
 */
export function useNuovaVersione(): boolean {
  const [disponibile, setDisponibile] = useState(false)

  useEffect(() => {
    const mia = impronta(import.meta.url)
    // In sviluppo il modulo non ha impronta: non c'è niente da confrontare.
    if (!mia) return

    let vivo = true

    const controlla = async () => {
      if (!vivo || document.visibilityState === 'hidden') return
      try {
        const risposta = await fetch(`${import.meta.env.BASE_URL}index.html`, { cache: 'no-store' })
        if (!risposta.ok) return
        const pubblicata = impronta(await risposta.text())
        if (vivo && pubblicata && pubblicata !== mia) setDisponibile(true)
      } catch {
        // Rete assente o server in aggiornamento: si riprova al giro dopo, in silenzio.
      }
    }

    const ogniTanto = setInterval(controlla, 5 * 60 * 1000)
    const alRitorno = () => {
      if (document.visibilityState === 'visible') void controlla()
    }
    document.addEventListener('visibilitychange', alRitorno)
    void controlla()

    return () => {
      vivo = false
      clearInterval(ogniTanto)
      document.removeEventListener('visibilitychange', alRitorno)
    }
  }, [])

  return disponibile
}

/** L'impronta del pacchetto, dal nome del file: `index-BoTNG-xL.js` → `BoTNG-xL`. */
function impronta(testo: string): string | null {
  return /assets\/index-([A-Za-z0-9_-]+)\.js/.exec(testo)?.[1] ?? null
}
